import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Info, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { AI_TOP_MATCHES_STAGES as STAGES, getRunProgress } from "@/hooks/jobs/useAITopMatches";
import type { AITopMatchesRun, AITopMatchesStage } from "@/hooks/jobs/useAITopMatches";
import { AITopMatchesBrief } from "./AITopMatchesBrief";

interface AITopMatchesProgressProps {
	run: AITopMatchesRun;
	isStale: boolean;
	onCancel: () => void;
	onResume: () => void;
}

export function AITopMatchesProgress({ run, isStale, onCancel, onResume }: AITopMatchesProgressProps) {
	const { t } = useTranslation();
	const { currentIndex, done, total, overall } = getRunProgress(run);

	const detail = (stage: AITopMatchesStage) => {
		if (stage === "screen" && run.stage === "screen" && total > 0)
			return t("aiTopMatches.progress.screened", { done, total });
		if (stage === "screen" && currentIndex > 1 && run.stats?.screened)
			return t("aiTopMatches.progress.screenedDone", { count: run.stats.screened });
		if (stage === "scout" && currentIndex > 2 && run.stats?.nominated !== undefined)
			return t("aiTopMatches.progress.nominated", { count: run.stats.nominated });
		if (stage === "deep" && run.stage === "deep" && total > 0)
			return t("aiTopMatches.progress.reviewed", { done, total });
		return null;
	};

	return (
		<>
			<div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
				<div className="space-y-2">
					<div className="flex items-center justify-between text-xs">
						<span className="font-medium text-foreground">
							{t("aiTopMatches.progress.step", { current: currentIndex + 1, total: STAGES.length })} · {t(`aiTopMatches.stages.${STAGES[Math.max(0, currentIndex)]}`)}
						</span>
						<span className="text-muted-foreground">{overall}%</span>
					</div>
					<Progress value={overall} className="h-1.5 rounded-sm" />
				</div>

				<ol className="rounded-sm border border-border">
					{STAGES.map((stage, index) => {
						const state = index < currentIndex ? "done" : index === currentIndex ? "current" : "pending";
						const text = detail(stage);
						return (
							<li
								key={stage}
								className={cn(
									"flex items-center gap-3 px-3 py-2.5",
									index > 0 && "border-t border-border",
									state === "current" && "bg-purple-50/60 dark:bg-purple-950/20",
								)}
							>
								<span
									className={cn(
										"flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
										state === "done" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
										state === "current" && "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
										state === "pending" && "bg-muted text-muted-foreground",
									)}
								>
									{state === "done" && <Check className="h-3.5 w-3.5" />}
									{state === "current" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
									{state === "pending" && index + 1}
								</span>
								<span className={cn("flex-1 text-sm", state === "pending" ? "text-muted-foreground" : "font-medium text-foreground")}>
									{t(`aiTopMatches.stages.${stage}`)}
								</span>
								{text && <span className="text-xs tabular-nums text-muted-foreground">{text}</span>}
							</li>
						);
					})}
				</ol>

				{run.brief && <AITopMatchesBrief brief={run.brief} stats={run.stats} />}

				{isStale && (
					<div className="flex items-center justify-between gap-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
						<span className="flex items-center gap-2">
							<AlertTriangle className="h-3.5 w-3.5" />
							{t("aiTopMatches.progress.stale")}
						</span>
						<Button size="sm" variant="outline" className="h-7" onClick={onResume}>
							<RotateCcw className="mr-1 h-3.5 w-3.5" />
							{t("aiTopMatches.progress.restart")}
						</Button>
					</div>
				)}
			</div>

			<div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-background px-5 py-3">
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Info className="h-3.5 w-3.5" />
					{t("aiTopMatches.progress.closeHint")}
				</span>
				<Button variant="outline" size="sm" onClick={onCancel}>
					<X className="mr-1 h-3.5 w-3.5" />
					{t("aiTopMatches.progress.cancel")}
				</Button>
			</div>
		</>
	);
}
